import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
} from 'react';
import { z } from 'zod';

import { codec, useQueryParams } from '@/hooks/router/use-query-params';

import { EditWorkstreamModal } from './edit-workstream.modal';

export type EditWorkstreamModalOpen = { id: string } | undefined;

const EditWorkstreamModalContext = createContext<
  (open: EditWorkstreamModalOpen) => void
>(() => {
  // This is a no-op function used as the default value for the context
  // It will be overridden by the actual provider
});

export function useEditWorkstreamModal() {
  return useContext(EditWorkstreamModalContext);
}

export function EditWorkstreamModalProvider({
  children,
}: PropsWithChildren<object>) {
  const [{ id }, setParams] = useQueryParams({
    id: {
      key: 'edit_workstream',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });

  const open = id ? { id } : undefined;

  const setOpen = useCallback(
    function (open: EditWorkstreamModalOpen) {
      setParams.replace({ id: open?.id });
    },
    [setParams],
  );

  return (
    <EditWorkstreamModalContext.Provider value={setOpen}>
      {children}
      <EditWorkstreamModal open={open} onClose={() => setOpen(undefined)} />
    </EditWorkstreamModalContext.Provider>
  );
}
